import React, { useRef, useState } from 'react';
import logo from 'media/Logo/SLogo.svg';

import SVG from 'components/renderSvg';
import { Link, useNavigate } from 'react-router-dom';
import { unregisterPushToken, switchToCustomer } from 'services/vendorAPI';
import Swal from 'sweetalert2';
import { useDispatch, useSelector } from 'react-redux';
import { togglesidebar } from 'store/slices/commonSlice';

import eye from 'media/svgs/eye.svg';
import edit from 'media/svgs/editprofile.svg';
import euro from 'media/svgs/gbp.svg';
import logout from 'media/svgs/log-out.svg';
import useWindowSize from 'hooks/useWindowSize';

import useOutsideClick from 'hooks/useOutsideClick';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { venderData } = useSelector(state => state.vender);
  const { isToggle } = useSelector(state => state.commonState);
  const dropdownRef = useRef(null);
  const { width } = useWindowSize();

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleToggleSidebar = () => {
    dispatch(togglesidebar());
  };

  const handleSwitchToCustomer = async () => {
    try {
      setIsOpen(false); // Close dropdown

      Swal.fire({
        title: 'Switching Perspective',
        text: 'Preparing your trainee view...',
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const response = await switchToCustomer();

      if (response.success) {
        const customerToken = response.data.customer_token;
        // Use environment variable or fallback to the provided customer domain
        const customerUrl = process.env.REACT_APP_CUSTOMER_APP_URL || 'https://app.sportona.com';

        // Redirect with the token as query param as per technical guide
        window.location.href = `${customerUrl}/login-redirect?token=${customerToken}`;
      } else {
        // Handle specific error codes if needed, though general error handling is fine
        Swal.fire({
          icon: 'error',
          title: 'Switch Failed',
          text: response.error || 'You cannot switch to trainee view at this time.',
          confirmButtonText: 'Understood',
          confirmButtonColor: '#7047EB'
        });
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'An unexpected error occurred during the switch.',
        confirmButtonColor: '#7047EB'
      });
    }
  };

  // Close dropdown when clicking outside
  useOutsideClick(dropdownRef, () => setIsOpen(false));

  const toggleDropdown = () => {
    setIsOpen(prev => !prev);
  };

  function getGreetingBasedOnTime() {
    const currentTime = new Date();
    const currentHour = currentTime.getHours();

    if (currentHour < 12) {
      return 'Morning';
    } else if (currentHour >= 12 && currentHour < 17) {
      return 'Afternoon';
    } else {
      return 'Evening';
    }
  }

  const DropdownMenu = () => {
    let profileStatus = 0;
    if (venderData?.addBatch) profileStatus += 20;
    if (venderData?.addHall) profileStatus += 20;
    if (venderData?.addTrainer) profileStatus += 20;
    if (venderData?.profileCompleted) profileStatus += 20;
    if (venderData?.serviceCompleted) profileStatus += 20;
    if (profileStatus > 100) profileStatus = 100;

    return (
      <div
        className='absolute right-4 top-[74px] w-[300px] bg-white rounded-2xl shadow-float z-50 border border-gray-100 p-2 overflow-hidden animate-slide-up origin-top-right'
        ref={dropdownRef}
      >
        <div className='flex items-center gap-3 p-3 bg-surface-50 rounded-xl mb-2'>
          <div className='flex-shrink-0'>
            {venderData?.photoURL ? (
              <img
                src={`${venderData?.photoURL}`}
                alt='user'
                className='w-12 h-12 rounded-full object-cover border-2 border-white shadow-sm'
              />
            ) : (
              <div className='w-12 h-12 rounded-full bg-brand-500 text-white flex items-center justify-center text-lg font-bold shadow-sm'>
                {venderData?.email?.charAt(0)?.toUpperCase()}
              </div>
            )}
          </div>
          <div className='flex flex-col min-w-0'>
            <p className='text-sm font-bold text-gray-900 truncate'>
              {venderData?.business_legal_name || 'Sportona User'}
            </p>
            <p className='text-xs text-gray-500 truncate'>{venderData?.email}</p>
            <div className="mt-1">
              {venderData?.subscription_tier === 'pro' ? (
                <span className='inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-brand-100 text-brand-800'>
                  Pro Plan
                </span>
              ) : (
                <span className='inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800'>
                  Trial / Free
                </span>
              )}
            </div>
          </div>
        </div>

        <div className='flex flex-col space-y-1'>
          {profileStatus === 100 && (
            <a
              href={`https://app.sportona.com/vendor/${venderData?.id}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setIsOpen(false)}
              className='flex items-center space-x-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors group'
            >
              <span className="p-2 bg-gray-100 rounded-lg group-hover:bg-white group-hover:shadow-sm transition-all"><SVG icon={eye} className="w-4 h-4 text-gray-600" /></span>
              <span className='text-sm font-medium text-gray-700 group-hover:text-gray-900'>View Public Profile</span>
            </a>
          )}
          <Link to={'/profile'} onClick={() => setIsOpen(false)} className='flex items-center space-x-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors group'>
            <span className="p-2 bg-gray-100 rounded-lg group-hover:bg-white group-hover:shadow-sm transition-all"><SVG icon={edit} className="w-4 h-4 text-gray-600" /></span>
            <span className='text-sm font-medium text-gray-700 group-hover:text-gray-900'>Edit Profile</span>
          </Link>

          <button
            onClick={handleSwitchToCustomer}
            className='flex items-center space-x-3 px-3 py-2.5 rounded-xl hover:bg-brand-50 transition-colors group w-full text-left'
          >
            <span className="p-2 bg-brand-100/50 rounded-lg group-hover:bg-brand-100 transition-all"><SVG icon={eye} className="w-4 h-4 text-brand-600" /></span>
            <span className='text-sm font-medium text-gray-700 group-hover:text-brand-700'>Preview as Trainee</span>
          </button>
          <Link to={'/plans'} onClick={() => setIsOpen(false)} className='flex items-center space-x-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors group'>
            <span className="p-2 bg-gray-100 rounded-lg group-hover:bg-white group-hover:shadow-sm transition-all"><SVG icon={euro} className="w-4 h-4 text-gray-600" /></span>
            <span className='text-sm font-medium text-gray-700 group-hover:text-gray-900'>Plans & Billing</span>
          </Link>
        </div>

        <div className="my-2 border-t border-gray-100"></div>

        <button
          onClick={async () => {
            const fcmToken = localStorage.getItem('fcm_token');
            if (fcmToken) {
              await unregisterPushToken(fcmToken);
            }
            localStorage.removeItem('token');
            localStorage.clear();
            navigate('/login');
          }}

          className='w-full flex items-center space-x-3 px-3 py-2.5 rounded-xl hover:bg-red-50 transition-colors group text-left'
        >
          <span className="p-2 bg-red-100/50 rounded-lg group-hover:bg-red-100 transition-all"><SVG icon={logout} className="w-4 h-4 text-red-600" /></span>
          <span className='text-sm font-medium text-red-600 group-hover:text-red-700'>Logout</span>
        </button>

        <div className="mt-2 px-3 py-2 text-center text-[10px] text-gray-400">
          Sportona v0.1 Beta • <Link to="/terms" className="hover:underline">Terms</Link> • <Link to="/privacy" className="hover:underline">Privacy</Link>
        </div>
      </div>
    );
  };

  return (
    <>
      <div
        className={`fixed w-full left-0 top-0 z-50 bg-white border-b border-gray-100 flex h-[64px] items-center justify-between px-4 sm:px-6 transition-all duration-300`}
      >
        {/* Mobile hamburger + Logo Area */}
        <div className="flex items-center gap-3">
          {/* Hamburger button - only on mobile */}
          <button
            onClick={handleToggleSidebar}
            className="md:hidden flex flex-col justify-center items-center w-9 h-9 rounded-lg hover:bg-gray-100 transition-colors focus:outline-none"
            aria-label="Toggle sidebar"
          >
            <span className={`block w-5 h-0.5 bg-gray-700 transition-all duration-300 ${!isToggle ? 'rotate-45 translate-y-1.5' : ''}`}></span>
            <span className={`block w-5 h-0.5 bg-gray-700 my-1 transition-all duration-300 ${!isToggle ? 'opacity-0' : ''}`}></span>
            <span className={`block w-5 h-0.5 bg-gray-700 transition-all duration-300 ${!isToggle ? '-rotate-45 -translate-y-1.5' : ''}`}></span>
          </button>

          {/* Logo */}
          <div
            className={`flex items-center gap-2 cursor-pointer transition-opacity hover:opacity-80`}
            onClick={() => navigate('/')}
          >
            <img src={logo} alt='logo' className="h-8 w-auto" />
            <span className="text-[18px] font-semibold tracking-tight text-gray-900">Sportona
              {/* Small (trademark registered) text below the logo */}
              <span className="text-xs font-normal text-gray-500">
                <span className="text-gray-500">®</span>
              </span>
            </span>
          </div>
        </div>

        {/* Right Actions Area */}

        <div className='flex-1 flex justify-end items-center gap-6'>
          {width > 800 && (
            <div className='flex flex-col items-end mr-4'>
              <span className='text-xs font-semibold text-gray-400 uppercase tracking-wider'>
                {getGreetingBasedOnTime()}
              </span>
              <span className='text-sm font-bold text-gray-800'>
                {venderData?.business_legal_name || 'Stranger'}
              </span>
            </div>
          )}
        </div>


        {/* Profile Dropdown Area */}
        <div className='flex items-center gap-4'>
          <div className='relative' ref={dropdownRef}>
            <button
              onClick={toggleDropdown}
              className="focus:outline-none transition-transform active:scale-95"
            >
              {venderData?.photoURL ? (
                <img
                  src={`${venderData?.photoURL}`}
                  alt='profile'
                  className={`w-9 h-9 rounded-full object-cover border-2 transition-all ${isOpen ? 'border-gray-400' : 'border-gray-200'}`}
                />
              ) : (
                <div
                  className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm transition-all ${isOpen ? 'bg-gray-800' : 'bg-gray-700'}`}
                >
                  {venderData?.email?.charAt(0)?.toUpperCase()}
                </div>
              )}
            </button>
            {isOpen && <DropdownMenu />}
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
